import * as React from "react";
import { useAuth } from "./auth-context";
import { loadAuth, saveAuth, apiFetch } from "@/lib/api-client";
import { parseJWTClaims } from "@/lib/jwt-claims";

/**
 * Phase 18.E — license-status helper.
 *
 * Server-side gilt: orgs.license_expires_at < now → /v1/(rooms|entries|...)-
 * mutations + verify+refresh-handler returnen 403 license_expired (middleware
 * RequireActiveLicense). Frontend nutzt denselben wert (über JWT-claim lic_exp,
 * 15min-staleness via refresh-rotation) um:
 *
 *   - persistenten ReadOnlyBanner zu rendern
 *   - mutation-CTAs visuell zu disablen (vermeidet click-fail-toast)
 *
 * Read-routes + DSGVO-export bleiben backend-side ungated — UI muss diese
 * auch bei "expired" weiterhin erreichbar lassen.
 *
 * B10 — fast-reactivation: `refreshLicenseStatus()` erzwingt einen
 * sofortigen refresh-rotation-roundtrip (statt bis zu ~15min auf die
 * proaktive token-rotation zu warten), damit nach einer verlängerung der
 * schreibzugriff zeitnah zurückkommt. Backend bleibt single-source-of-truth:
 * der mutation-gate (license_gate / RequireActiveLicense) entscheidet
 * server-seitig — dieser pfad aktualisiert nur den client-cache, gated NICHTS.
 */
export type LicenseStatus = "active" | "expired" | "no_enforcement";

/**
 * Reine funktion für tests / cross-component-checks ohne hook-overhead.
 * `licenseExpiresAt` ist unix-seconds (matched JWT-claim shape).
 * `nowSec` als parameter (statt Date.now() intern) → deterministisch testbar.
 */
export function getLicenseStatus(
  licenseExpiresAt: number | undefined,
  nowSec: number = Math.floor(Date.now() / 1000),
): LicenseStatus {
  if (licenseExpiresAt === undefined || licenseExpiresAt === 0) {
    return "no_enforcement";
  }
  return licenseExpiresAt > nowSec ? "active" : "expired";
}

/**
 * React-hook für den banner-mount + button-gating.
 * Subscribed via auth-context useLiveQuery → flippt sofort wenn die
 * refresh-rotation einen neuen lic_exp in dexie schreibt (z.B. nach
 * zahlungs-eingang + admin-reaktivierung).
 */
export function useLicenseStatus(): LicenseStatus {
  const auth = useAuth();
  return getLicenseStatus(auth.user?.licenseExpiresAt);
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * B10 — erzwingt EINEN refresh-rotation-roundtrip und schreibt den
 * frisch geparsten `lic_exp` zurück in dexie. Repliziert bewusst die
 * rotation aus api-client.tryRefresh() (dort private), weil der normale
 * request-pfad nur bei <2min access-token-restlaufzeit rotiert — nach
 * einer lizenz-verlängerung ist das access-token aber meist noch gültig,
 * sodass ohne expliziten roundtrip bis zu ~15min stale lic_exp bliebe.
 *
 * Contract-annahme (be-gates): nach verlängerung trägt das rotierte
 * access-token den neuen lic_exp (analog trial-extension / zahlungs-eingang,
 * siehe api-client.ts → "refresh-rotation kann einen neuen lic_exp tragen").
 * Der server-mutation-gate bleibt autoritativ; dieser pfad cached nur.
 *
 * Returns den ermittelten LicenseStatus nach rotation. Bei netzwerk-/auth-
 * fehler: returnt den unveränderten aktuellen status (kein throw → der
 * aufrufende button bleibt benutzbar, user kann erneut tippen).
 */
export async function refreshLicenseStatus(): Promise<LicenseStatus> {
  const current = await loadAuth();
  if (!current) return "no_enforcement";

  try {
    const resp = await apiFetch<RefreshResponse>("/auth/refresh", {
      method: "POST",
      body: { refresh_token: current.refreshToken },
      anonymous: true,
      noRefresh: true,
    });
    const claims = parseJWTClaims(resp.access_token);
    await saveAuth({
      ...current,
      accessToken: resp.access_token,
      refreshToken: resp.refresh_token,
      accessTokenExpiresAt: Date.now() + resp.expires_in * 1000,
      user: {
        ...current.user,
        licenseExpiresAt: claims.lic_exp,
      },
    });
    return getLicenseStatus(claims.lic_exp);
  } catch {
    // rotation fehlgeschlagen (offline / server / token-family-revoke):
    // status unverändert lassen, kein throw — UI bleibt bedienbar.
    return getLicenseStatus(current.user.licenseExpiresAt);
  }
}

/**
 * Hook-wrapper um refreshLicenseStatus() mit pending-state für den
 * "Schreibzugriff aktualisieren"-button. `useLiveQuery` im auth-context
 * flippt useLicenseStatus() automatisch, sobald saveAuth() den neuen
 * lic_exp committed — dieser hook liefert nur das pending-flag + den
 * trigger, kein eigener status-state nötig (single-source bleibt dexie).
 */
export function useLicenseRefresh(): {
  refresh: () => Promise<LicenseStatus>;
  isRefreshing: boolean;
} {
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = React.useCallback(async (): Promise<LicenseStatus> => {
    setIsRefreshing(true);
    try {
      return await refreshLicenseStatus();
    } finally {
      if (mounted.current) setIsRefreshing(false);
    }
  }, []);

  return { refresh, isRefreshing };
}
